import { QuoteLanding } from "./QuoteLanding";
import {
    Button, ClickEvent, Component, CrudDecorator, DataSourceExecutionEvent, DataSourceMode, Layout,
    TableRow, TableRowDisplayEvent, TableSelectionEvent
} from "@mcleod/components";
import { CurrencyUtil, DateRange, DateUtil, DisplayType, DisplayValue, getThemeColor, ModelRow, StringUtil, UrlUtil } from "@mcleod/core";
import { ModelQuoteCustomer, OrderTabView } from "@mcleod/dispatch";
import { AutogenLayoutQuoteList } from "./autogen/AutogenLayoutQuoteList";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { LMECompanySettings, LMEGeneralSettings } from "@mcleod/general";

export class QuoteList extends AutogenLayoutQuoteList {

    private _customerName: string = null;

    override onLoad() {
        this.panelQuoteListContainer.rows[0].style.height = "100%"; // pretty nasty hack to make the vertical scroll bar behave
        this.textboxOrderMode.visible = LMEGeneralSettings.getSingleton().getDispatchControl()["enable_manifesting"] === "Y" || LMECompanySettings.getSingleton().isAssetLtl();
        this.addLayoutLoadListener(() => {
            this.textboxId.focus();
        })
        const urlProp = UrlUtil.getPropsFromUrl(window.location.search);
        if (urlProp != null && urlProp.carrier_name != null) {
            this._customerName = urlProp.carrier_name
        }
    }

    get customerName(): string {
        return this._customerName;
    }

    set customerName(value: string) {
        this._customerName = value;
        if (!StringUtil.isEmptyString(value)) {
            const safeValue = value.replace(/'/g, "''");
            this.sourceQuoteOrder.searchRow.setValueAndLookupModelData("customer_name", safeValue, { "name": safeValue });
            this.textboxCustomerName.displayData(this.sourceQuoteOrder.searchRow, null, 0);
            const defaultDateRange = DateRange.parseDateRangeWithKeywords("t-30,t");
            this.textboxOrderedDate.text = DisplayValue.getDisplayValue(defaultDateRange, DisplayType.DATERANGE, this.textboxOrderedDate.format);
            this.sourceQuoteOrder.searchRow.set("offer_date", defaultDateRange);
            this.sourceQuoteOrder.executeValidatedSearch();
        }
    }

    tableQuotesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        tableRow.forEveryChildComponent(comp => {
            this.setTooltipContent(comp, tableRow.data);
            if (comp.id == "buttonCreateOrder") {
                const expirationDate = modelRow.get("expiration_date");
                const quoteStatus = modelRow.get("quote_status")
                QuoteLanding.checkEnableConvertToOrder(comp, new Date(expirationDate), quoteStatus);
            }
        });
        if (modelRow.getBoolean("is_brokerage_ltl")) {
            tableRow.findComponentById("buttonCreateOrder").visible = false;
        }
        const isExpired = modelRow.getDate("expiration_date")?.valueOf() < Date.now();
        const labelEffectiveDate = tableRow.findComponentById("labelEffectiveDate");
        labelEffectiveDate.color = isExpired ? getThemeColor("error") : getThemeColor("default");
    }

    setTooltipContent(comp: Component, modelRow: ModelRow) {
        if (comp.id == null) return;
        switch (comp.id) {
            case "labelEffectiveDate":
                return CommonTooltips.setTooltipContentTextboxes(comp, () => [{
                    caption: "Expiration Date",
                    displayType: DisplayType.DATE,
                    text: DateUtil.formatDate(modelRow.getDate("expiration_date"), "MM/dd/yyyy"),
                }]);
            case "labelOriginLocationName":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("shipper.location_id"), "lme/dispatch/LocationQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelDestinationLocationName":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("consignee.location_id"), "lme/dispatch/LocationQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "textboxCustName":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("customer_id"), "lme/ar/CustomerQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelTotalCharge":
                return CommonTooltips.setTooltipContentTextboxes(comp, () => [{
                    caption: "Freight Charge",
                    displayType: DisplayType.CURRENCY,
                    text: CurrencyUtil.formatCurrency(modelRow.get("freight_charge"))
                }, {
                    caption: "Other Charges",
                    displayType: DisplayType.CURRENCY,
                    text: CurrencyUtil.formatCurrency(modelRow.get("otherchargetotal"))
                }])
        }
    }

    /** This is an event handler for the onClick event of buttonCreateOrder for each row in tableQuoteList.  */
    buttonCreateOrderOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Button);
        const modelRow = tableRow.data as ModelRow;
        const quoteId = modelRow.getString("id");
        const customerId = modelRow.getString("customer_id", null);
        if (!customerId) {
            this.openQuoteAsOrder(quoteId);
        } else {
            new ModelQuoteCustomer().searchSingle({ id: customerId }).then(customerRow => {
                if ("P" == customerRow?.getString("type_of", null)) {
                    CommonDialogs.showError("Prospect type must be updated to customer to convert quote to an order. ", "Invalid Customer");
                    return;
                }
                this.openQuoteAsOrder(quoteId);
            });
        }
    }

    openQuoteAsOrder(quote_id: string) {
        const layout = Layout.getLayout("lme/dispatch/OrderTabView") as OrderTabView;
        layout["additionalProps"] = { quote_id };
        new CrudDecorator({
            layout: layout,
            mode: DataSourceMode.ADD,
            pseudoNavigateUrl: "lme/dispatch/OrderTabView",
            headerProps: { showClose: true }
        }).slideIn();
    }

    /** This is an event handler for the onSelect event of tableQuoteList. */
    tableQuoteListOnSelect(event: TableSelectionEvent) {
        if (event.newRow?.data?.getBoolean("is_brokerage_ltl") === true) {
            this.tableQuoteList.detailLayout =  "lme/dispatch/brltl/QuoteLTL";
        } else {
            this.tableQuoteList.detailLayout =  "lme/powerbroker/QuoteLanding";
        }
    }
}
